import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import { useNavigate } from 'react-router-dom';
import { Field, Form } from 'react-final-form';
import InputFieldComponent from 'components/InputField';
import validations from 'validations';
import AuthContext from 'providers/Auth/Auth.context';
import PasswordField from 'components/PasswordField';
import AuthPageWrapper from 'components/AuthPageWrapper';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import './Login.styles.scss';

type LoginProps = Record<string, any>;

const Login: React.FC<LoginProps> = ({ className }) => {
  const { t } = useTranslation();
  const { loginRequest, twoFactorLoginRequest } = useContext(AuthContext);
  const [userId, setUserId] = useState(null);

  const navigate = useNavigate();
  const classes = classNames('login-page', className);

  const handleLogin = useCallback(
    (values: { email: string; password: string }) => {
      loginRequest(values, (data) => {
        if (data.userId) setUserId(data.userId);
        else navigate('/auth');
      });
    },
    [loginRequest, navigate],
  );

  const handleVerify = useCallback(
    (values: { code: string }) => {
      twoFactorLoginRequest({ userId, verificationCode: values.code }, () => {
        navigate('/dash');
      });
    },
    [navigate, twoFactorLoginRequest, userId],
  );

  return (
    <AuthPageWrapper>
      {!userId ? (
        <Form
          onSubmit={handleLogin}
          render={({ handleSubmit, submitting, valid }) => (
            <form className={classes} onSubmit={handleSubmit}>
              <Field
                name="email"
                component={InputFieldComponent}
                label={t('email-label')}
                placeholder={t('email-placeholder')}
                validate={validations.emailValidation()}
                required
              />
              <PasswordField
                name="password"
                label={t('password')}
                placeholder={t('login-password-placeholder')}
              />
              <Link to="/forgot-password">{t('forgot-password')}</Link>
              <Button type="submit" disabled={submitting || !valid}>
                {t('login')}
              </Button>
            </form>
          )}
        />
      ) : (
        <Form
          onSubmit={handleVerify}
          render={({ handleSubmit, submitting, valid }) => (
            <form className={classes} onSubmit={handleSubmit}>
              <p>{t('login-two-factor-paragraph')}</p>
              <Field
                name="code"
                component={InputFieldComponent}
                label={t('code-label')}
                placeholder={t('code-placeholder')}
                maxLength={4}
                required
              />
              <Button type="submit" disabled={submitting || !valid}>
                {t('verify')}
              </Button>
            </form>
          )}
        />
      )}
    </AuthPageWrapper>
  );
};

export default Login;
